import type { StyleProp, TextStyle, ViewStyle } from 'react-native'
import { useMemo } from 'react'
import { StyleSheet, Text, View } from 'react-native'

import type { MarkdownToken } from './markdown-parsing'
import { colors } from '../../../theme'
import { typography } from '../../../theme/typography'
import { openExternalUrl } from '../../../utils/external-url'
import { parseInlineMarkdown, parseMarkdownBlocks } from './markdown-parsing'

interface MarkdownTextProps {
  content: string
  style?: StyleProp<ViewStyle>
  paragraphStyle?: StyleProp<TextStyle>
  paragraphSpacingStyle?: StyleProp<TextStyle>
  linkStyle?: StyleProp<TextStyle>
  boldStyle?: StyleProp<TextStyle>
  italicStyle?: StyleProp<TextStyle>
}

const MARKDOWN_LIST_BULLET_WIDTH = 21
const MARKDOWN_LIST_ITEM_SPACING = 12
const MARKDOWN_LIST_BULLET_OFFSET = 7

interface MarkdownTokenContentProps {
  boldStyle: StyleProp<TextStyle>
  handleOpenLink: (url: string) => void
  italicStyle: StyleProp<TextStyle>
  linkStyle: StyleProp<TextStyle>
  tokens: MarkdownToken[]
}

function MarkdownTokenContent({
  boldStyle,
  handleOpenLink,
  italicStyle,
  linkStyle,
  tokens,
}: MarkdownTokenContentProps) {
  return tokens.map((token, tokenIndex) => {
    if (token.type === 'link') {
      return (
        <Text
          key={`token-${tokenIndex}`}
          style={[
            token.bold ? boldStyle : undefined,
            token.italic ? italicStyle : undefined,
            linkStyle,
          ]}
          onPress={() => {
            handleOpenLink(token.url)
          }}
        >
          {token.label}
        </Text>
      )
    }

    return (
      <Text
        key={`token-${tokenIndex}`}
        style={[
          token.bold ? boldStyle : undefined,
          token.italic ? italicStyle : undefined,
        ]}
      >
        {token.value}
      </Text>
    )
  })
}

export function MarkdownText({
  content,
  style,
  paragraphStyle = [typography.body3, { color: colors.textBody }],
  paragraphSpacingStyle = { marginBottom: 12 },
  linkStyle = [
    typography.body2,
    { color: colors.primary, textDecorationLine: 'underline' },
  ],
  boldStyle = { fontFamily: 'OpenSans_700Bold', fontWeight: '700' },
  italicStyle = { fontStyle: 'italic' },
}: MarkdownTextProps) {
  const blocks = useMemo(() => parseMarkdownBlocks(content), [content])
  const listSpacingStyle = useMemo(() => {
    const flattened = StyleSheet.flatten(paragraphSpacingStyle)
    if (flattened == null) return undefined

    return {
      marginTop: flattened.marginTop,
      marginBottom: flattened.marginBottom,
      marginVertical: flattened.marginVertical,
      margin: flattened.margin,
    } satisfies ViewStyle
  }, [paragraphSpacingStyle])
  const parsedBlocks = useMemo(
    () =>
      blocks.map((block) =>
        block.type === 'paragraph'
          ? {
              type: 'paragraph' as const,
              tokens: parseInlineMarkdown(block.content),
            }
          : {
              type: 'list' as const,
              ordered: block.ordered,
              items: block.items.map((item) => parseInlineMarkdown(item)),
            },
      ),
    [blocks],
  )

  const handleOpenLink = async (url: string) => {
    try {
      await openExternalUrl(url)
    } catch (error) {
      console.warn('Failed to open markdown link:', error)
    }
  }

  if (parsedBlocks.length === 0) return null

  return (
    <View style={style}>
      {parsedBlocks.map((block, blockIndex) => {
        const isLastBlock = blockIndex === parsedBlocks.length - 1

        if (block.type === 'paragraph') {
          return (
            <Text
              key={`paragraph-${blockIndex}`}
              style={[
                paragraphStyle,
                !isLastBlock ? paragraphSpacingStyle : undefined,
              ]}
            >
              <MarkdownTokenContent
                tokens={block.tokens}
                boldStyle={boldStyle}
                italicStyle={italicStyle}
                linkStyle={linkStyle}
                handleOpenLink={(url) => {
                  void handleOpenLink(url)
                }}
              />
            </Text>
          )
        }

        return (
          <View
            key={`list-${blockIndex}`}
            style={!isLastBlock ? listSpacingStyle : undefined}
          >
            {block.items.map((itemTokens, itemIndex) => (
              <View
                key={`list-item-${itemIndex}`}
                style={{
                  flexDirection: 'row',
                  alignItems: 'flex-start',
                  marginBottom:
                    itemIndex < block.items.length - 1
                      ? MARKDOWN_LIST_ITEM_SPACING
                      : 0,
                }}
              >
                <Text
                  style={[
                    paragraphStyle,
                    {
                      width: MARKDOWN_LIST_BULLET_WIDTH,
                      paddingLeft: MARKDOWN_LIST_BULLET_OFFSET,
                    },
                  ]}
                >
                  {block.ordered ? `${itemIndex + 1}.` : '\u2022'}
                </Text>
                <Text style={[paragraphStyle, { flex: 1 }]}>
                  <MarkdownTokenContent
                    tokens={itemTokens}
                    boldStyle={boldStyle}
                    italicStyle={italicStyle}
                    linkStyle={linkStyle}
                    handleOpenLink={(url) => {
                      void handleOpenLink(url)
                    }}
                  />
                </Text>
              </View>
            ))}
          </View>
        )
      })}
    </View>
  )
}
